import React, { useCallback, useMemo } from 'react';
import { cloneDeep, set } from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useProductCostingState } from '../ProductCostingContext';
import { specificationRequirementFieldPath } from '../fieldPath';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import BombiixAGGrid from '../../../../Components/BombiixAGGrid';
import type { BombiixColumnDefOrGroup, BombiixRowId } from '../../../../Components/BombiixAGGrid/types';
import { gridHeight } from './costingRowHeights';
import { blankSpecificationRequirementRow } from '../specification-requirements.helpers';
import type { SpecificationRequirementRow, SpecificationRequirementState } from '../types';

/** Width of the trailing action column, matching Figma 13601:75409. */
const ACTIONS_COLUMN_WIDTH = 66;

/**
 * Rows shown before the grid starts scrolling.
 *
 * Its own constant rather than `SIDE_MAX_ROWS` (4) or `CORE_MAX_ROWS` (9): this is a full-width card
 * and five rows was the requested depth.
 */
const SPEC_REQUIREMENTS_MAX_ROWS = 5;

/** The redux slices this table reads. */
interface SpecificationRequirementsRootState {
  Users: { logInRights?: unknown };
}

/**
 * One rendered row.
 *
 * A `type`, not an `interface`: BombiixAGGrid constrains its row generic to `Record<string, unknown>`
 * and only a type alias picks up the implicit index signature that satisfies it.
 */
type SpecificationRequirementGridRow = SpecificationRequirementRow & {
  __cagId: string;
};

/**
 * The Spec Requirements card on a product costing (BM-9899).
 *
 * The Costing Template declares WHICH specifications a product must be measured against; this card is
 * where they are answered. Rows seeded from the template have a locked Requirement and an editable
 * Specification; rows the user adds here are editable in both columns, which is how the design's
 * "Specification Met" row exists without being in the template.
 *
 * Whether this renders at all is decided by the parent (`ProductCosting.tsx`) from the costing's
 * embedded template snapshot — the same gate the Set-up Cost and Additional Component cards use.
 */
export function SpecificationRequirementsTable(): React.JSX.Element {
  const { t: tCost } = useTranslation('costing');
  const { productCostingstate, specificationRequirementState, setSpecificationRequirementState } = useProductCostingState();
  const { productTemplate } = productCostingstate;
  const {
    specificationRequirement: { specificationRequirementInfo },
  } = specificationRequirementState;

  const {
    Users: { logInRights },
  } = useSelector((prevState: SpecificationRequirementsRootState) => prevState);
  /* UserRights.js declares `CheckActionPermission(rights, section, module = null, action = null)`, so
     TypeScript infers the last two parameters as `null` from their defaults. The cast restores the
     signature the function actually accepts — the same shim the sibling tables use. */
  const { CheckActionPermission } = useComponent() as {
    CheckActionPermission: (rights: unknown, section: string, module?: string | null, action?: string | null) => boolean;
  };
  const CoastingAddEditRights = CheckActionPermission(logInRights, 'products', 'costing', 'addEdit');
  const { statusState } = useShowHideSectionHooks();

  /*
   * Never renders an empty grid. With no template requirements and nothing saved there would be no
   * row to type into and — because the `+` is drawn on the LAST row — no way to add one either, which
   * is what left the card blank. The fallback row is display-only: lodash `set` in `handleValueChange`
   * creates the real entry at index 0 the moment anything is typed into it.
   */
  const sourceRows = useMemo<SpecificationRequirementRow[]>(
    () => (Array.isArray(specificationRequirementInfo) && specificationRequirementInfo.length > 0 ? specificationRequirementInfo : [blankSpecificationRequirementRow()]),
    [specificationRequirementInfo],
  );

  const rowData = useMemo<SpecificationRequirementGridRow[]>(
    () =>
      sourceRows.map((row, index) => ({
        ...(row as SpecificationRequirementGridRow),
        __cagId: (row?._id as string | undefined) || `idx_${index}`,
      })),
    [sourceRows],
  );

  const indexFromRowId = useCallback((rowId: BombiixRowId) => rowData.findIndex((row) => row.__cagId === rowId), [rowData]);

  const handleValueChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      const index = rowData.findIndex((row) => row.__cagId === rowId);
      if (index < 0) return;
      const copyState: SpecificationRequirementState = cloneDeep(specificationRequirementState);
      const paths = specificationRequirementFieldPath(index);
      set(copyState, field === 'requirement' ? paths.REQUIREMENT : paths.SPECIFICATION, value);
      setSpecificationRequirementState((prevState) => ({ ...prevState, ...copyState }));
    },
    [rowData, specificationRequirementState, setSpecificationRequirementState],
  );

  /**
   * Appends a blank user-owned row.
   *
   * When state is still empty the on-screen fallback row is materialised first, so `+` grows the list
   * from one row to two rather than appearing to do nothing.
   */
  const addNewRow = useCallback((): void => {
    setSpecificationRequirementState((prevState) => {
      const existing = prevState.specificationRequirement.specificationRequirementInfo;
      const base = existing.length > 0 ? existing : [blankSpecificationRequirementRow()];
      return {
        ...prevState,
        specificationRequirement: {
          ...prevState.specificationRequirement,
          specificationRequirementInfo: [...base, blankSpecificationRequirementRow()],
        },
      };
    });
  }, [setSpecificationRequirementState]);

  /**
   * Removes one row, template-seeded or not.
   *
   * A seeded row deleted here is gone from the saved costing, but a template refresh rebuilds it —
   * the template owns which requirements exist. That matches Additional Components.
   */
  const removeRow = useCallback(
    (rowId: BombiixRowId): void => {
      const index = indexFromRowId(rowId);
      if (index < 0) return;
      const copyState: SpecificationRequirementState = cloneDeep(specificationRequirementState);
      copyState.specificationRequirement.specificationRequirementInfo.splice(index, 1);
      setSpecificationRequirementState((prevState) => ({ ...prevState, ...copyState }));
    },
    [indexFromRowId, specificationRequirementState, setSpecificationRequirementState],
  );

  const columnDefs = useMemo<BombiixColumnDefOrGroup<SpecificationRequirementGridRow>[]>(
    () => [
      {
        field: 'requirement',
        headerName: tCost('COSTING_TEMPLATE.COSTING_COLUMNS.SPECIFICATION_REQUIREMENTS.REQUIREMENT'),
        cellType: 'text',
        flex: 1,
        minWidth: 200,
        sortable: false,
        /* Locked for a row the template supplied — the template owns that wording. A row the user
           added here is theirs to name. */
        getCellReadOnly: (row) => statusState || Boolean(row.isFromTemplate),
        getCellDisabled: () => !CoastingAddEditRights,
        cellProps: { fallback: '' },
      },
      {
        field: 'specification',
        headerName: tCost('COSTING_TEMPLATE.COSTING_COLUMNS.SPECIFICATION_REQUIREMENTS.SPECIFICATION'),
        cellType: 'text',
        flex: 1,
        minWidth: 200,
        sortable: false,
        fillable: true,
        getCellReadOnly: () => statusState,
        getCellDisabled: () => !CoastingAddEditRights,
        cellProps: { fallback: '' },
      },
    ],
    [tCost, statusState, CoastingAddEditRights],
  );

  const showActions = !statusState && CoastingAddEditRights;
  /* `exactOptionalPropertyTypes` rejects an explicit `undefined` on these optional props, so the
     conditional spread stands in for `showActions ? handler : undefined`. Same result. */
  const rowActionProps = showActions ? { onAddRow: addNewRow, onDeleteRow: removeRow } : {};

  /*
   * Laid out like the other FULL-WIDTH sections (Testing, Detailed Costing, Grand Total): a plain
   * `costing-card` with its own title row and an explicitly-sized grid. No `CostingCardShell`, so no
   * expand-to-full-page control — this section does not have one.
   *
   * The height must be explicit pixels. `.costing-grid-fill` + `height="100%"` — the side-column
   * idiom — resolves only inside the flex chain established by
   * `.costing-2col-grid > .col-xl-6 > .costing-card > .costing-card-body`. This card sits outside
   * that grid, so a percentage measured against nothing and the whole grid, column headers included,
   * collapsed to zero height.
   */
  return (
    <div className="costing-card p-12px">
      <div className="d-flex align-items-center mb-8">
        <p className="product-cp-title mb-0 mr-8">
          {(productTemplate?.specificationRequirementTitle as string | undefined) ||
            tCost('COSTING_TEMPLATE.COSTING_COLUMNS.SPECIFICATION_REQUIREMENTS.CARD_TITLE')}
        </p>
      </div>
      <BombiixAGGrid<SpecificationRequirementGridRow>
        fullWidthHorizontalScroll
        gridOptions={{ suppressMovableColumns: true, rowHeight: 40 }}
        hideCellErrorMessage
        revealHeaderControlsOnHover
        sortable={false}
        showColumnMenu={false}
        enableFillHandle
        columnDefs={columnDefs}
        rowData={rowData}
        getRowId={(row) => row.__cagId}
        onRowChange={handleValueChange}
        rowActionsWidth={ACTIONS_COLUMN_WIDTH}
        {...rowActionProps}
        height={gridHeight(Math.min(rowData.length, SPEC_REQUIREMENTS_MAX_ROWS))}
      />
    </div>
  );
}

export default SpecificationRequirementsTable;
